import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { functions } from './firebase';
import { httpsCallable } from 'firebase/functions';

function RegistrationSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [playerData, setPlayerData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setError('Invalid session. Please try registering again.');
      setLoading(false);
      return;
    }

    // Verify the session with our backend
    const verifySession = async () => {
      try {
        const verify = httpsCallable(functions, 'verifyStripeSession');
        const result = await verify({ sessionId });
        
        if (result.data.success && result.data.paid) {
          setVerified(true);
          setPlayerData(result.data);
        } else {
          setError('Payment not completed. Please try again.');
        }
      } catch (err) {
        console.error('Verification error:', err);
        setError('Could not verify payment. If you were charged, please contact support.');
      } finally {
        setLoading(false);
      }
    };

    verifySession();
  }, [searchParams]);

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#111827',
      color: 'white',
      padding: '48px 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    content: {
      maxWidth: '500px',
      textAlign: 'center',
    },
    successIcon: {
      fontSize: '72px',
      marginBottom: '24px',
    },
    title: {
      fontSize: '32px',
      fontWeight: 'bold',
      marginBottom: '16px',
    },
    subtitle: {
      fontSize: '16px',
      color: '#9ca3af',
      lineHeight: '1.6',
      marginBottom: '32px',
    },
    card: {
      backgroundColor: '#1f2937',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '24px',
      textAlign: 'left',
    },
    infoRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '12px',
      fontSize: '14px',
    },
    label: {
      color: '#9ca3af',
    },
    value: {
      fontWeight: '500',
    },
    button: {
      width: '100%',
      padding: '16px 24px',
      backgroundColor: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      marginBottom: '12px',
    },
    secondaryButton: {
      width: '100%',
      padding: '14px 24px',
      backgroundColor: 'transparent',
      color: '#9ca3af',
      border: '1px solid #4b5563',
      borderRadius: '10px',
      fontSize: '14px',
      cursor: 'pointer',
    },
    appLinks: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'center',
      marginTop: '24px',
    },
    appBadge: {
      height: '44px',
      cursor: 'pointer',
    },
    spinner: {
      width: '48px',
      height: '48px',
      border: '3px solid #374151',
      borderTopColor: '#10b981',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    },
    errorIcon: {
      fontSize: '72px',
      marginBottom: '24px',
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.spinner}></div>
          <p style={{ marginTop: '24px', color: '#9ca3af' }}>Verifying your payment...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.errorIcon}>❌</div>
          <h1 style={styles.title}>Something went wrong</h1>
          <p style={styles.subtitle}>{error}</p>
          <button onClick={() => navigate('/player-registration')} style={styles.button}>
            Try Again
          </button>
          <button onClick={() => navigate('/')} style={styles.secondaryButton}>
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const expiryDate = playerData?.subscriptionExpiry 
    ? new Date(playerData.subscriptionExpiry).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    : 'Unknown';

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <div style={styles.successIcon}>🎉</div>
        <h1 style={styles.title}>Welcome to Premium!</h1>
        <p style={styles.subtitle}>
          Your subscription is now active. You'll receive push notifications 
          whenever your matches are assigned to tables.
        </p>

        <div style={styles.card}>
          <div style={styles.infoRow}>
            <span style={styles.label}>Player</span>
            <span style={styles.value}>{playerData?.playerName}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.label}>Email</span>
            <span style={styles.value}>{playerData?.email}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.label}>Valid until</span>
            <span style={styles.value}>{expiryDate}</span>
          </div>
        </div>

        <div style={{ backgroundColor: '#1f2937', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>
            📱 Download the App
          </h3>
          <p style={{ fontSize: '14px', color: '#9ca3af', marginBottom: '16px' }}>
            To receive notifications, download the Cue Clock Pro app and sign in with your phone number.
          </p>
          
          <div style={styles.appLinks}>
            <a href="https://apps.apple.com/app/cue-clock-pro" target="_blank" rel="noopener noreferrer">
              <img 
                src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" 
                alt="Download on App Store" 
                style={styles.appBadge}
              />
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.cueclockpro" target="_blank" rel="noopener noreferrer">
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" 
                alt="Get it on Google Play" 
                style={styles.appBadge}
              />
            </a>
          </div>
        </div>

        <button onClick={() => navigate('/')} style={styles.secondaryButton}>
          Go to Homepage
        </button>
      </div>
    </div>
  );
}

export default RegistrationSuccess;
